import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "@/lib/api";

const newToken = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now());

// Address type-ahead powered by Google Places Autocomplete (New) via our backend proxy
// (session-token billing; key stays server-side). onSelect returns {display_name, lat, lon}.
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  className = "",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const skipNext = useRef(false);
  const timer = useRef(null);
  const tokenRef = useRef(newToken());

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (!value || value.trim().length < 3) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: value, token: tokenRef.current } });
        setResults(Array.isArray(data.suggestions) ? data.suggestions : []);
        setOpen(true); setActive(-1);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [value]);

  const choose = async (p) => {
    skipNext.current = true;
    onChange(p.text);
    setResults([]); setOpen(false); setActive(-1);
    try {
      const { data } = await api.get("/places/details", { params: { place_id: p.place_id, token: tokenRef.current } });
      onSelect?.({ display_name: data.address || p.text, lat: data.lat, lon: data.lon });
    } catch {
      onSelect?.({ display_name: p.text });
    }
    tokenRef.current = newToken(); // close this billing session, start a fresh one
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => { e.target.select(); results.length && setOpen(true); }}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={className}
        data-testid={testid}
        autoComplete="off"
      />
      {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && results.length > 0 && (
        <ul className="absolute z-[60] left-0 right-0 mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-xl max-h-64 overflow-auto" data-testid={testid ? `${testid}-suggestions` : "address-suggestions"}>
          {results.map((p, i) => (
            <li key={p.place_id}
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              onMouseEnter={() => setActive(i)}
              className={`flex items-start gap-2 px-3 py-2 text-sm cursor-pointer ${i === active ? "bg-navy/10" : ""}`}
              data-testid={testid ? `${testid}-option-${i}` : undefined}>
              <MapPin className="w-4 h-4 text-brandred shrink-0 mt-0.5" />
              <span className="text-navy leading-snug">{p.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
